import React, { useState } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 20px;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 30px;
  color: #333;
`;

const HelpCard = styled.div`
  background: white;
  border-radius: 0;
  padding: 30px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
`;

const SectionTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
`;

const FAQItem = styled.div`
  border-bottom: 1px solid #e0e0e0;
  padding: 20px 0;
  
  &:last-child {
    border-bottom: none;
  }
`;

const FAQQuestion = styled.div<{ $isOpen: boolean }>`
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  &:hover {
    color: #007AFF;
  }
`;

const FAQAnswer = styled.div<{ $isOpen: boolean }>`
  font-size: 0.9rem;
  color: #666;
  margin-top: 15px;
  line-height: 1.6;
  max-height: ${props => props.$isOpen ? '200px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

const ToggleText = styled.span<{ $isOpen: boolean }>`
  font-size: 1rem;
  font-weight: 600;
  color: #007AFF;
  transition: all 0.3s ease;
  
  &:hover {
    color: #0056CC;
  }
`;

const StepList = styled.ol`
  padding-left: 20px;
  margin: 15px 0;
`;

const StepItem = styled.li`
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 10px;
  line-height: 1.6;
`;


const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-top: 20px;
`;

const FeatureCard = styled.div`
  background: #f8f8f8;
  border-radius: 0;
  padding: 20px;
  text-align: center;
`;


const FeatureTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
`;

const FeatureDescription = styled.p`
  font-size: 0.9rem;
  color: #666;
`;

const ContactSection = styled.div`
  padding: 30px;
  margin-top: 20px;
  text-align: center;
`;

const ContactHeader = styled.div`
  margin-bottom: 20px;
`;

const ContactTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 700;
  color: #333;
  margin: 0;
`;

const ContactEmail = styled.a`
  display: inline-block;
  padding: 12px 24px;
  border: 1px solid #4f4f4f;
  border-radius: 0;
  transition: all 0.2s ease-in;
  position: relative;
  overflow: hidden;
  font-size: 19px;
  cursor: pointer;
  color: black;
  z-index: 1;
  text-decoration: none;
  margin: 15px 0;
  
  &::before {
    content: "";
    position: absolute;
    left: 50%;
    transform: translateX(-50%) scaleY(1) scaleX(1.25);
    top: 100%;
    width: 140%;
    height: 180%;
    background-color: rgba(0, 0, 0, 0.05);
    border-radius: 50%;
    display: block;
    transition: all 0.5s 0.1s cubic-bezier(0.55, 0, 0.1, 1);
    z-index: -1;
  }
  
  &::after {
    content: "";
    position: absolute;
    left: 55%;
    transform: translateX(-50%) scaleY(1) scaleX(1.45);
    top: 180%;
    width: 160%;
    height: 190%;
    background-color: #0066ff;
    border-radius: 50%;
    display: block;
    transition: all 0.5s 0.1s cubic-bezier(0.55, 0, 0.1, 1);
    z-index: -1;
  }
  
  &:hover {
    color: #ffffff;
    border: 1px solid #0066ff;
  }
  
  &:hover::before {
    top: -35%;
    background-color: #0066ff;
    transform: translateX(-50%) scaleY(1.3) scaleX(0.8);
  }
  
  &:hover::after {
    top: -45%;
    background-color: #0066ff;
    transform: translateX(-50%) scaleY(1.3) scaleX(0.8);
  }
`;

const ContactDescription = styled.p`
  font-size: 0.95rem;
  color: #666;
  margin: 0;
  font-style: italic;
`;

const CopiedMessage = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: black;
  color: white;
  padding: 12px 24px;
  border-radius: 0;
  font-size: 16px;
  font-weight: 600;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
`;

const HelpScreen: React.FC = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };
  
  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText('hello@accordwp.com');
      setShowCopiedMessage(true);
      setTimeout(() => {
        setShowCopiedMessage(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy email:', err);
    }
  };

  const faqs = [
    {
      question: 'How to generate wallpapers with Accord?',
      answer: 'Generating wallpapers with Accord is very simple: 1. Upload your phone case photo 2. Click analyze image 3. Select generation style 4. Click generate wallpaper. The entire process takes about 30 seconds.'
    },
    {
      question: 'What image formats are supported?',
      answer: 'Accord supports common image formats including JPG, PNG, GIF, BMP and WebP. We recommend using high-quality images for better generation results.'
    },
    {
      question: 'Do generated wallpapers have watermarks?',
      answer: 'Preview versions of wallpapers will have watermarks. To get high-quality wallpapers without watermarks, you need to purchase the corresponding service. We offer multiple payment options.'
    },
    {
      question: 'How to save generated wallpapers?',
      answer: 'After generation is complete, you can click the download button to save wallpapers locally. All generated wallpapers are also automatically saved to your history.'
    },
    {
      question: 'Can I customize generation styles?',
      answer: 'Yes, in addition to preset style options, you can also enter special requirements in custom prompts, and AI will adjust the generation effect according to your description.'
    },
    {
      question: 'How to contact customer service?',
      answer: 'You can contact us via email, online customer service or social media. We will reply to your questions within 24 hours.'
    }
  ];

  const features = [
    {
      title: 'AI Smart Analysis',
      description: 'Uses advanced AI technology to analyze your phone case style'
    },
    {
      title: 'Multiple Styles',
      description: 'Provides similar, inverted, minimalist, artistic and other generation styles'
    },
    {
      title: 'Privacy Protection',
      description: 'Strictly protects your image privacy and will not leak to third parties'
    },
    {
      title: 'Fast Generation',
      description: 'Complete wallpaper generation in 30 seconds without long waiting'
    }
  ];

  return (
    <Container>
      <Title>Help Center</Title>

      {/* Feature Introduction */}
      <HelpCard>
        <SectionTitle>Features</SectionTitle>
        <FeatureGrid>
          {features.map((feature, index) => (
            <FeatureCard key={index}>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
            </FeatureCard>
          ))}
        </FeatureGrid>
      </HelpCard>


      {/* FAQ */}
      <HelpCard>
        <SectionTitle>Frequently Asked Questions</SectionTitle>
        {faqs.map((faq, index) => (
          <FAQItem key={index}>
            <FAQQuestion 
              $isOpen={openFAQ === index}
              onClick={() => toggleFAQ(index)}
            >
              {faq.question}
              <ToggleText $isOpen={openFAQ === index}>
                {openFAQ === index ? 'Hide' : 'Show'}
              </ToggleText>
            </FAQQuestion>
            <FAQAnswer $isOpen={openFAQ === index}>
              {faq.answer}
            </FAQAnswer>
          </FAQItem>
        ))}
      </HelpCard>

      {/* Contact Us */}
      <HelpCard id="contact">
        <SectionTitle>Contact Us</SectionTitle>
        <ContactSection>
          <ContactEmail onClick={handleCopyEmail}>
            hello@accordwp.com
          </ContactEmail>
          <ContactDescription>
            We'll get back to you within 24 hours
          </ContactDescription>
          {showCopiedMessage && (
            <CopiedMessage>
              Email <span style={{ color: '#0066ff' }}>copied</span> to clipboard!
            </CopiedMessage>
          )}
        </ContactSection>
      </HelpCard>

    </Container>
  );
};

export default HelpScreen;
